import type { FC, ReactNode } from 'react'
import type { Schematic, SchematicEdge, SchematicNode } from '@/types'

import { cn } from '@/lib/utils'

/*
 * Technical "plates": architecture diagrams drawn from a small spec in the
 * content registry. Nodes sit on a grid (x/y are the node centre in grid
 * cells), edges route orthogonally, and every color comes from the theme
 * tokens, so a plate inside a dark section inverts on its own.
 */

const CELL_W = 100
const CELL_H = 84
const PAD = 28

type Box = {
    left: number
    right: number
    top: number
    bottom: number
    cx: number
    cy: number
    width: number
    height: number
}

type Route = {
    d: string
    label: {
        x: number
        y: number
        anchor: 'start' | 'middle' | 'end'
    }
}

const nodeBox = (node: SchematicNode, compact: boolean): Box => {
    const width = (node.w ?? 1.4) * CELL_W
    const height = compact ? 30 : node.sub ? 50 : 38
    const cx = PAD + node.x * CELL_W
    const cy = PAD + node.y * CELL_H

    return {
        left: cx - width / 2,
        right: cx + width / 2,
        top: cy - height / 2,
        bottom: cy + height / 2,
        cx,
        cy,
        width,
        height
    }
}

const round = (value: number): number => Math.round(value * 10) / 10

const route = (a: Box, b: Box): Route => {
    const GAP = 12

    if (b.left >= a.right + GAP || b.right <= a.left - GAP) {
        const toRight = b.left >= a.right + GAP
        const sx = toRight ? a.right : a.left
        const ex = toRight ? b.left - 1 : b.right + 1
        const sy = a.cy
        const ey = b.cy

        if (Math.abs(sy - ey) < 1) {
            return {
                d: `M${round(sx)} ${round(sy)}H${round(ex)}`,
                label: { x: round((sx + ex) / 2), y: round(sy - 8), anchor: 'middle' }
            }
        }

        const mx = (sx + ex) / 2

        return {
            d: `M${round(sx)} ${round(sy)}H${round(mx)}V${round(ey)}H${round(ex)}`,
            label: { x: round(toRight ? mx + 7 : mx - 7), y: round((sy + ey) / 2 + 3), anchor: toRight ? 'start' : 'end' }
        }
    }

    const below = b.top >= a.bottom
    const sy = below ? a.bottom : a.top
    const ey = below ? b.top - 1 : b.bottom + 1
    const overlapLeft = Math.max(a.left, b.left)
    const overlapRight = Math.min(a.right, b.right)

    if (overlapRight - overlapLeft > 16) {
        const x = (overlapLeft + overlapRight) / 2

        return {
            d: `M${round(x)} ${round(sy)}V${round(ey)}`,
            label: { x: round(x + 7), y: round((sy + ey) / 2 + 3), anchor: 'start' }
        }
    }

    const my = (sy + ey) / 2

    return {
        d: `M${round(a.cx)} ${round(sy)}V${round(my)}H${round(b.cx)}V${round(ey)}`,
        label: { x: round((a.cx + b.cx) / 2), y: round(my - 7), anchor: 'middle' }
    }
}

const labelBox = (label: string, x: number, y: number, anchor: Route['label']['anchor']) => {
    const width = label.length * 6.1 + 8
    const left = anchor === 'start' ? x - 4 : anchor === 'end' ? x - width + 4 : x - width / 2

    return { x: round(left), y: round(y - 10), width: round(width), height: 14 }
}

const describe = (schematic: Schematic): string => {
    const names = new Map(schematic.nodes.map((node) => [node.id, node.label]))
    const lines = schematic.edges.map((edge: SchematicEdge) => {
        const from = names.get(edge.from) ?? edge.from
        const to = names.get(edge.to) ?? edge.to

        return `${from} to ${to}${edge.label ? ` (${edge.label})` : ''}`
    })

    return `${schematic.caption.replace(/\.\s*$/, '')}. Connections: ${lines.join('; ')}.`
}

interface SchematicDiagramProps {
    schematic: Schematic
    id: string
    compact?: boolean
    className?: string
}

/** The SVG itself. `compact` drops all text for use as a listing cover. */
export const SchematicDiagram: FC<SchematicDiagramProps> = ({ schematic, id, compact = false, className }): ReactNode => {
    const width = PAD * 2 + schematic.cols * CELL_W
    const height = PAD * 2 + schematic.rows * CELL_H
    const boxes = new Map(schematic.nodes.map((node) => [node.id, nodeBox(node, compact)]))
    const markerId = `${id}-arrow`
    const accentMarkerId = `${id}-arrow-accent`
    const strokeWidth = compact ? 1.6 : 1

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            className={cn('text-foreground', className)}
            role={compact ? undefined : 'img'}
            aria-hidden={compact ? 'true' : undefined}
            aria-label={compact ? undefined : describe(schematic)}
            preserveAspectRatio='xMidYMid meet'
        >
            <defs>
                <marker id={markerId} viewBox='0 0 8 8' refX='7' refY='4' markerWidth='8' markerHeight='8' markerUnits='userSpaceOnUse' orient='auto'>
                    <path d='M0 0.6 L7 4 L0 7.4' fill='none' className='stroke-foreground' strokeWidth='1.2' />
                </marker>
                <marker id={accentMarkerId} viewBox='0 0 8 8' refX='7' refY='4' markerWidth='8' markerHeight='8' markerUnits='userSpaceOnUse' orient='auto'>
                    <path d='M0 0.6 L7 4 L0 7.4' fill='none' className='stroke-brand' strokeWidth='1.4' />
                </marker>
            </defs>

            {schematic.groups?.map((group) => {
                const x = PAD + group.x * CELL_W
                const y = PAD + group.y * CELL_H

                return (
                    <g key={group.label}>
                        <rect
                            x={x}
                            y={y}
                            width={group.w * CELL_W}
                            height={group.h * CELL_H}
                            className='fill-none stroke-border-strong'
                            strokeWidth={strokeWidth}
                            strokeDasharray='5 4'
                        />
                        {!compact && (
                            <text
                                x={x + 10}
                                y={y + 17}
                                className='fill-muted-foreground font-mono uppercase'
                                fontSize='9.5'
                                letterSpacing='0.8'
                            >
                                {group.label}
                            </text>
                        )}
                    </g>
                )
            })}

            {schematic.edges.map((edge) => {
                const a = boxes.get(edge.from)
                const b = boxes.get(edge.to)

                if (!a || !b) return null

                const target = schematic.nodes.find((node) => node.id === edge.to)
                const accent = target?.kind === 'accent'
                const path = route(a, b)
                const box = edge.label ? labelBox(edge.label, path.label.x, path.label.y, path.label.anchor) : null

                return (
                    <g key={`${edge.from}-${edge.to}`}>
                        <path
                            d={path.d}
                            fill='none'
                            className={accent ? 'stroke-brand' : 'stroke-foreground'}
                            strokeOpacity={accent ? 1 : 0.75}
                            strokeWidth={strokeWidth}
                            strokeDasharray={edge.dashed ? '4 3' : undefined}
                            markerEnd={`url(#${accent ? accentMarkerId : markerId})`}
                        />

                        {!compact && edge.label && box && (
                            <>
                                <rect x={box.x} y={box.y} width={box.width} height={box.height} className='fill-surface' />
                                <text
                                    x={path.label.x}
                                    y={path.label.y}
                                    textAnchor={path.label.anchor}
                                    className='fill-muted-foreground font-mono'
                                    fontSize='10'
                                >
                                    {edge.label}
                                </text>
                            </>
                        )}
                    </g>
                )
            })}

            {schematic.nodes.map((node) => {
                const box = boxes.get(node.id)

                if (!box) return null

                const kind = node.kind ?? 'service'

                return (
                    <g key={node.id}>
                        {kind === 'store' ? (
                            <>
                                <path
                                    d={`M${box.left} ${box.top + 6} A${box.width / 2} 6 0 0 1 ${box.right} ${box.top + 6} V${box.bottom - 6} A${box.width / 2} 6 0 0 1 ${box.left} ${box.bottom - 6} Z`}
                                    className={compact ? 'fill-surface-strong stroke-foreground' : 'fill-background stroke-foreground'}
                                    strokeWidth={strokeWidth}
                                />
                                <ellipse
                                    cx={box.cx}
                                    cy={box.top + 6}
                                    rx={box.width / 2}
                                    ry='6'
                                    className='fill-surface stroke-foreground'
                                    strokeWidth={strokeWidth}
                                />
                            </>
                        ) : (
                            <rect
                                x={box.left}
                                y={box.top}
                                width={box.width}
                                height={box.height}
                                className={cn(
                                    kind === 'accent'
                                        ? compact ? 'fill-brand stroke-brand' : 'fill-background stroke-brand'
                                        : 'fill-background stroke-foreground'
                                )}
                                strokeWidth={kind === 'accent' && !compact ? 1.5 : strokeWidth}
                                strokeDasharray={kind === 'actor' ? '4 3' : undefined}
                            />
                        )}

                        {kind === 'accent' && !compact && (
                            <rect x={box.right - 9} y={box.top + 4} width='5' height='5' className='fill-brand' />
                        )}

                        {!compact && (
                            <>
                                <text
                                    x={box.cx}
                                    y={node.sub ? box.cy - 2 : box.cy + 4}
                                    textAnchor='middle'
                                    className={cn('font-mono', kind === 'accent' ? 'fill-brand' : 'fill-foreground')}
                                    fontSize='11.5'
                                    fontWeight='500'
                                >
                                    {node.label}
                                </text>

                                {node.sub && (
                                    <text
                                        x={box.cx}
                                        y={box.cy + 14}
                                        textAnchor='middle'
                                        className='fill-muted-foreground font-mono uppercase'
                                        fontSize='8.5'
                                        letterSpacing='0.6'
                                    >
                                        {node.sub}
                                    </text>
                                )}
                            </>
                        )}
                    </g>
                )
            })}

            {!compact && (
                <g className='stroke-foreground' strokeWidth='1'>
                    <path d={`M8 18 V8 H18`} fill='none' />
                    <path d={`M${width - 18} 8 H${width - 8} V18`} fill='none' />
                    <path d={`M8 ${height - 18} V${height - 8} H18`} fill='none' />
                    <path d={`M${width - 18} ${height - 8} H${width - 8} V${height - 18}`} fill='none' />
                </g>
            )}
        </svg>
    )
}

/** Dotted engineering-paper ground, drawn once per plate. */
export const DotGrid: FC<{ id: string, className?: string }> = ({ id, className }): ReactNode => {
    return (
        <svg aria-hidden='true' className={cn('absolute inset-0 size-full', className)}>
            <defs>
                <pattern id={`${id}-dots`} width='18' height='18' patternUnits='userSpaceOnUse'>
                    <rect x='0' y='0' width='1.2' height='1.2' className='fill-border-strong' />
                </pattern>
            </defs>
            <rect width='100%' height='100%' fill={`url(#${id}-dots)`} />
        </svg>
    )
}

interface SchematicFigureProps {
    schematic: Schematic
    id: string
    figure?: string
    title?: string
    className?: string
}

/** Full labelled plate with figure number, heading and caption. */
const SchematicFigure: FC<SchematicFigureProps> = ({ schematic, id, figure = '01', title = 'System overview', className }): ReactNode => {
    return (
        <figure className={cn('border border-border bg-surface', className)}>
            <div className='flex items-center justify-between gap-4 border-b border-border px-4 py-2.5 font-mono text-label uppercase text-muted-foreground'>
                <span>
                    <span className='text-foreground'>Fig. {figure}</span>
                    <span className='mx-2 text-border-strong'>/</span>
                    {title}
                </span>
                <span aria-hidden='true' className='hidden sm:inline'>Plate</span>
            </div>

            <div className='overflow-x-auto' tabIndex={0} aria-label='Diagram, scroll horizontally on small screens'>
                <div className='relative min-w-[34rem]'>
                    <DotGrid id={id} />
                    <SchematicDiagram schematic={schematic} id={id} className='relative block h-auto w-full' />
                </div>
            </div>

            <figcaption className='border-t border-border px-4 py-3 text-[0.875rem] leading-relaxed text-muted-foreground'>
                {schematic.caption}
            </figcaption>
        </figure>
    )
}

export default SchematicFigure
